// relay. PWA runtime: service-worker registration, update detection and install-prompt capture.
// Kept framework-light so it can be imported once from index.js.

let deferredPrompt = null;
const installListeners = new Set();
const updateListeners = new Set();

export function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function isIOS() {
  const ua = window.navigator.userAgent || "";
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ reports as Mac; detect touch to disambiguate.
  const iPadOS = ua.includes("Macintosh") && "ontouchend" in document;
  return iOSDevice || iPadOS;
}

export function canPromptInstall() {
  return !!deferredPrompt;
}

export function onInstallAvailability(cb) {
  installListeners.add(cb);
  cb(canPromptInstall());
  return () => installListeners.delete(cb);
}
function emitInstall() {
  installListeners.forEach((cb) => cb(canPromptInstall()));
}

export async function promptInstall() {
  if (!deferredPrompt) return "unavailable";
  deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice.catch(() => ({ outcome: "dismissed" }));
  deferredPrompt = null;
  emitInstall();
  return choice.outcome; // "accepted" | "dismissed"
}

export function onUpdateReady(cb) {
  updateListeners.add(cb);
  return () => updateListeners.delete(cb);
}
function emitUpdate(worker) {
  updateListeners.forEach((cb) => cb(worker));
}

export function registerPwa() {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    emitInstall();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    emitInstall();
  });

  if (!("serviceWorker" in navigator)) return;

  // If a controller already exists, a controllerchange later means a NEW build took over
  // (an update) — reload once to drop any stale shell. First-ever installs have no
  // controller, so we must NOT reload then (avoids a needless first-visit reload).
  const hadController = !!navigator.serviceWorker.controller;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((reg) => {
        // A worker is already waiting (update installed on a previous visit).
        if (reg.waiting && navigator.serviceWorker.controller) emitUpdate(reg.waiting);

        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            if (nw.state === "installed" && navigator.serviceWorker.controller) {
              emitUpdate(nw); // new build ready, old one still controlling
            }
          });
        });
      })
      .catch(() => {});

    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing || !hadController) return;
      refreshing = true;
      window.location.reload();
    });
  });
}

export function applyUpdate(worker) {
  if (worker) worker.postMessage({ type: "SKIP_WAITING" });
}
